// Formatting prices for the paywall.
//
// The server sends the currency code, the symbol and the decimal mark this
// market uses, and this puts them together. Deliberately NOT `Intl.NumberFormat`
// keyed off the browser locale: the price is a fact about the ACCOUNT's market,
// and a Canadian actor browsing with a Spanish locale is still charged CAD with
// a dot. Locale-formatting a foreign currency produces confident-looking
// nonsense like "1.790,00 $" for a Mexican price shown to a French browser.
//
// It mirrors React-Membership's `setNumberToCurrency` (which also swaps the
// decimal mark by hand) with one difference: thousands are separated, because
// "MXN $1790" reads as a typo next to "MXN $1,790".

export interface IMoneyFormat {
  currencyCode: string;
  currencySymbol: string;
  // "." or "," — whichever this market writes decimals with.
  fractional: string;
}

export interface IMoneyOptions {
  // Drop the decimals entirely. For a rounded figure like the annual saving,
  // where "save $98.40" is precision nobody asked for.
  whole?: boolean;
  // Omit the ISO code, leaving just the symbol and the number.
  symbolOnly?: boolean;
}

const groupThousands = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const formatMoney = (
  amount: number,
  format: IMoneyFormat,
  options: IMoneyOptions = {}
): string => {
  const safe = isFinite(amount) ? amount : 0;
  const fractional = format.fractional || ".";

  // Decimals only when there are any, which is what makes "$99" read as a round
  // number and "$8.25" as an exact one.
  const showDecimals = !options.whole && Math.round(safe * 100) % 100 !== 0;
  const fixed = showDecimals ? safe.toFixed(2) : String(Math.round(safe));
  const [whole, decimals] = fixed.split(".");

  const number = decimals
    ? `${groupThousands(whole)}${fractional}${decimals}`
    : groupThousands(whole);

  const withSymbol = `${format.currencySymbol || ""}${number}`;
  if (options.symbolOnly || !format.currencyCode) return withSymbol;
  // "CAD $129" — the code matters here because several markets use "$" and the
  // visitor is deciding whether to hand over a card.
  return `${format.currencyCode} ${withSymbol}`;
};
